import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { PFLICHT_THEMEN, PFLICHT_REZERT, PFLICHT_RAHMEN } from "@/data/pflichtfortbildungen";

const TEASER_THEMEN = PFLICHT_THEMEN.slice(0, 6);

/** Pflichtfortbildungen · Kinetic Editorial — Espresso-Szene mit nummeriertem Themen-Index. */
export default function PflichtfortbildungenSection() {
  return (
    <section
      id="pflichtfortbildungen"
      data-testid="pflicht-section"
      className="relative bg-espresso text-cream-50 py-28 md:py-40 overflow-hidden"
    >
      {/* grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Editorial Opener ── */}
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono text-[11px] tracking-[0.2em] text-saffron">Nº 08</span>
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-cream-100/70">
                  Pflichtfortbildungen · Für Träger &amp; Einrichtungen
                </span>
              </div>
              <h2 className="font-magazine font-medium leading-[1.08] text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem]">
                Ein Plan. Alle Pflichten.<br />
                <span className="text-saffron">MD-prüfungsfest dokumentiert.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-cream-100/75 text-[14.5px] leading-[1.75] lg:text-right">
                Viele Einrichtungen planen pro Mitarbeitendem jährlich{" "}
                <strong className="text-cream-50">{PFLICHT_RAHMEN.basisStunden} Fortbildungsstunden</strong>,
                Praxisanleitende zusätzlich {PFLICHT_RAHMEN.praxisanleiterStunden} Stunden.
                Wir bündeln die häufigsten Pflichtthemen — online, jeden Montag.
              </p>
            </div>
          </div>
        </Reveal>

        {/* ── FP-Regel als Editorial-Fact-Strip ── */}
        <Reveal delay={100}>
          <div className="flex flex-wrap items-center gap-4 border-y border-cream-50/15 py-5 mb-2" data-testid="pflicht-fp-strip">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.26em] font-bold text-saffron">
              <ShieldCheck className="h-4 w-4" strokeWidth={1.8} />
              FP-System
            </span>
            <span className="font-mono text-[12px] tracking-[0.06em] text-cream-100/80">
              {PFLICHT_RAHMEN.fpRegel} · Zertifikat mit UE + FP · direkt für die MD-Akte
            </span>
          </div>
        </Reveal>

        {/* ── Themen-Index · nummerierte Editorial-Liste ── */}
        <div data-testid="pflicht-themen-grid">
          {TEASER_THEMEN.map((t, i) => (
            <Reveal key={t.slug} delay={i * 60}>
              <div
                data-testid={`pflicht-theme-${t.slug}`}
                className="group grid grid-cols-12 gap-4 lg:gap-8 items-baseline py-7 border-b border-cream-50/12 transition-colors duration-300 hover:bg-cream-50/5"
              >
                <div className="col-span-2 sm:col-span-1 font-mono text-[12px] tracking-[0.14em] text-terra pt-1.5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 sm:col-span-7">
                  <h3 className="font-magazine font-medium text-[1.5rem] md:text-[1.8rem] leading-none text-cream-50">
                    <span className="relative inline">
                      {t.title}
                      <span className="absolute left-0 -bottom-1 h-[2px] w-0 group-hover:w-full transition-[width] duration-500 bg-saffron" />
                    </span>
                  </h3>
                  <div className="mt-2 text-[10.5px] uppercase tracking-[0.24em] font-bold text-cream-100/50">
                    {t.audience}
                  </div>
                  <p className="mt-2 text-[13.5px] text-cream-100/70 leading-relaxed max-w-xl">{t.short}</p>
                </div>
                <div className="col-span-12 sm:col-span-4 flex sm:justify-end items-baseline gap-2">
                  <span className="font-magazine text-[2rem] md:text-[2.4rem] leading-none text-saffron">{t.ue}</span>
                  <span className="font-mono text-[11px] tracking-[0.12em] text-cream-100/60 uppercase">UE · {t.ue} FP</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ── Rezertifizierungen · Plakat-Duo ── */}
        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {PFLICHT_REZERT.map((r, i) => {
            const saffron = i % 2 === 1;
            return (
              <Reveal key={r.slug} delay={i * 120}>
                <div
                  data-testid={`pflicht-rezert-${r.slug}`}
                  className={`h-full flex flex-col p-7 md:p-8 ${saffron ? "bg-saffron text-espresso" : "bg-terra text-cream-50"}`}
                >
                  <div className={`text-[10.5px] uppercase tracking-[0.26em] font-bold ${saffron ? "text-terra" : "text-saffron"}`}>
                    {r.legal} · {r.interval}
                  </div>
                  <div className="mt-4 flex items-end justify-between gap-4 flex-1">
                    <h3 className="font-magazine font-medium text-[1.6rem] md:text-[1.9rem] leading-[1.08]">
                      {r.title}
                    </h3>
                    <div className="text-right shrink-0">
                      <div className="font-magazine text-[2.4rem] leading-none">{r.ue}</div>
                      <div className={`font-mono text-[10.5px] tracking-[0.12em] uppercase mt-1 ${saffron ? "text-espresso/70" : "text-cream-100/70"}`}>
                        UE / Jahr
                      </div>
                    </div>
                  </div>
                  <p className={`mt-4 text-[13.5px] leading-relaxed ${saffron ? "text-espresso/80" : "text-cream-100/85"}`}>
                    {r.short}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* ── CTAs ── */}
        <Reveal delay={200}>
          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <Link
              to="/pflichtfortbildungen"
              data-testid="pflicht-section-all-cta"
              className="inline-flex items-center gap-2 rounded-full bg-saffron text-espresso h-13 px-8 py-3.5 text-[12.5px] font-bold uppercase tracking-[0.12em] hover:bg-cream-50 transition-colors duration-300"
            >
              Alle Pflichtthemen ansehen
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
            <Link
              to="/pflegedienste#pakete"
              data-testid="pflicht-section-plan-cta"
              className="inline-flex items-center gap-2 text-[13px] font-bold text-cream-50 border-b-2 border-saffron pb-1 hover:text-saffron transition-colors"
            >
              Jahres-Fortbildungsplan ansehen &amp; direkt buchen
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
